import type { ReactNode } from 'react';
import { Icon } from '../../components/Icon';

/** Visual replicas of real Mealnex screens. All names and figures are illustrative. */

export function Ic({ name }: { name: string }) {
  return <span className="mk-ic"><Icon name={name} size={24} /></span>;
}

export type MockStatus = 'active' | 'pending' | 'frozen' | 'cancelled' | 'expired' | 'completed';
const statusLabels: Record<MockStatus, string> = { active: 'فعّال', pending: 'بانتظار الموافقة', frozen: 'مجمّد', cancelled: 'ملغي', expired: 'منتهي', completed: 'مكتمل' };

export function Badge({ status, children }: { status: MockStatus; children?: ReactNode }) {
  return <span className={`mk-badge mk-badge-${status}`}>{children ?? statusLabels[status]}</span>;
}

export function Screen({ label, className = '', children }: { label: string; className?: string; children: ReactNode }) {
  // role="img" keeps assistive tech from reading every fake row; the label describes the scene.
  return <div className={`mk-screen ${className}`.trim()} role="img" aria-label={label}>{children}</div>;
}

export function PhoneFrame({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mk-phone ${className}`.trim()}><div className="mk-phone-notch" /><div className="mk-phone-body">{children}</div></div>;
}

/* A decorative QR-like pattern. It is generated from a fixed seed and encodes nothing. */
function buildQrPath(size = 25, seed = 7) {
  let state = seed;
  const random = () => { state = (state * 1103515245 + 12345) % 2147483648; return state / 2147483648; };
  const finder = (x: number, y: number) => x < 7 && y < 7;
  const inFinder = (x: number, y: number) => finder(x, y) || finder(size - 1 - x, y) || finder(x, size - 1 - y);
  let path = '';
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    if (inFinder(x, y)) continue;
    if (x === 7 || y === 7 || x === size - 8 || y === size - 8) continue;
    if (random() > 0.52) path += `M${x} ${y}h1v1h-1z`;
  }
  return path;
}
const qrPath = buildQrPath();

export function QrPattern({ className = 'mk-qr' }: { className?: string }) {
  const size = 25;
  const eye = (x: number, y: number) => <g key={`${x}-${y}`}><path d={`M${x} ${y}h7v7h-7z M${x + 1} ${y + 1}v5h5v-5z`} fillRule="evenodd" /><path d={`M${x + 2} ${y + 2}h3v3h-3z`} /></g>;
  return <svg className={className} viewBox={`-2 -2 ${size + 4} ${size + 4}`} aria-hidden="true" focusable="false" shapeRendering="crispEdges">
    <rect x="-2" y="-2" width={size + 4} height={size + 4} fill="#fff" />
    <g fill="currentColor">{eye(0, 0)}{eye(size - 7, 0)}{eye(0, size - 7)}<path d={qrPath} /></g>
  </svg>;
}

export function Sidebar({ active }: { active: string }) {
  const items: [string, string][] = [['dashboard', 'الرئيسية'], ['scan', 'صرف وجبة'], ['card', 'الاشتراكات'], ['bell', 'طلبات الاشتراك'], ['box', 'طلبات التوصيل'], ['users', 'العملاء'], ['box', 'الباقات والأسعار'], ['invoice', 'الفواتير'], ['chart', 'التقارير'], ['store', 'الفروع']];
  return <aside className="mk-sidebar">
    <div className="mk-brand"><span className="mk-brand-mark">↗</span><span><b>ميلنكس</b><small>Mealnex</small></span></div>
    <div className="mk-tenant"><span>م</span><span><b>مطعمك</b><small>لوحة المطعم</small></span></div>
    <nav>{items.map(([icon, label]) => <span key={label} className={label === active ? 'is-active' : ''}><Ic name={icon} />{label}</span>)}</nav>
  </aside>;
}

export function Topbar({ title }: { title: string }) {
  return <div className="mk-topbar"><span><b>{title}</b><small>مطعمك</small></span><span className="mk-topbar-actions"><span className="mk-icon-btn"><Ic name="bell" /></span><span className="mk-chip">رابط التسجيل</span><span className="mk-avatar">ن</span></span></div>;
}
